export const DEFAULT_SITE_URL = "https://bansuri.app";

export const SEO_DEFAULTS = {
  title: "Bansuri Scale Chart and Fingering Explorer | bansuri.app",
  description:
    "Interactive bansuri scale chart with swara-to-note mapping, flute key transposition, and practical fingering guidance for riyaaz.",
  keywords: [
    "bansuri scale chart",
    "bansuri fingering chart",
    "bansuri notes",
    "hindustani swara chart",
    "bansuri transposition",
    "indian flute scales",
    "sa re ga ma",
  ],
  siteName: "bansuri.app",
  locale: "en_US",
  twitterCard: "summary_large_image",
  ogImagePath: "/logo.svg",
};

type FaqItem = {
  question: string;
  answer: string;
};

const FAQ_ITEMS: FaqItem[] = [
  {
    question: "How do I choose the right bansuri scale?",
    answer:
      "Select your flute key, then compare playable target scales and difficulty to match your song key and comfort level.",
  },
  {
    question: "Can I use this for major and minor scales?",
    answer:
      "Yes. The explorer supports major and natural minor scale mapping with swara labels and western note references.",
  },
  {
    question: "Does this tool show swara to western note mapping?",
    answer:
      "Yes. For each configuration, the table maps sa re ga ma pa dha ni to corresponding western notes.",
  },
];

export function normalizeSiteUrl(input?: string): string {
  const raw = (input ?? "").trim() || DEFAULT_SITE_URL;
  try {
    const parsed = new URL(raw);
    return parsed.origin;
  } catch {
    return DEFAULT_SITE_URL;
  }
}

export function buildStructuredData(siteUrl: string) {
  const faqEntities = FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  }));

  return [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SEO_DEFAULTS.siteName,
      url: siteUrl,
      inLanguage: "en",
      potentialAction: {
        "@type": "SearchAction",
        target: `${siteUrl}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Bansuri Scale Explorer",
      applicationCategory: "MusicApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      url: siteUrl,
      description: SEO_DEFAULTS.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqEntities,
    },
  ];
}

export function buildSitemapXml(siteUrl: string): string {
  const now = new Date().toISOString();
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${siteUrl}/</loc>
    <lastmod>${now}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>1.0</priority>
  </url>
</urlset>
`;
}

export function buildRobotsTxt(siteUrl: string): string {
  return `User-agent: *
Allow: /

Sitemap: ${siteUrl}/sitemap.xml
`;
}
